/* eslint-disable no-unused-vars */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';

import LockOpenIcon from '@mui/icons-material/LockOpen';
import { startRegistration } from '@simplewebauthn/browser';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import auth from '@/services/AuthService';
import useAppStore from '@/store/app';
import { useState } from 'react';
import { useEffect } from 'react';
import { Alert } from '@mui/material';

export default function SetWebauthn({
  onSetupSuccess,
  onSetupFailure,
  setSelectedOption,
}) {
  const [loading, setLoading] = useState(false);
  const { setDialogForm } = useAppStore();
  const theme = useTheme();

  const userToken = localStorage.getItem('userToken');
  const secureUID = localStorage.getItem('secureUID');

  const fetchRegistrationData = async () => {
    try {
      const res = await auth.get('/webauthn/registration/token', {
        params: {
          userToken: userToken,
          secureUID: secureUID,
        },
      });
      const { data } = res;
      return data;
    } catch (e) {
      onSetupFailure('Fehler beim Einrichten von Webauthn');
    }
    return null;
  };

  const handleRegister = async () => {
    setLoading(true);
    const registrationOptions = await fetchRegistrationData();
    if (!registrationOptions) return;
    try {
      const attResp = await startRegistration({
        optionsJSON: registrationOptions,
      });
      const verificationsResp = await auth.post(
        '/webauthn/registration/token',
        {
          ...attResp,
          userToken: userToken,
          secureUID: secureUID,
        },
      );
      const confirmResp = await auth.post('/confirm', {
        token: userToken,
        sessionId: secureUID,
      });
      if (verificationsResp.status === 204 && confirmResp.status === 204) {
        handleCloseDialog();
        onSetupSuccess();
        toast.success(<ToastMessage title="Gerät erfolgreich registriert" />);
      } else {
        onSetupFailure("Fehler beim Einrichten von Webauthn'");
      }
    } catch (e) {
      // Delete temporary registration options.
      await auth.post('/webauthn/deleteRegistrationOptions/token', {
        userToken: userToken,
        secureUID: secureUID,
      });
      toast.error(
        <ToastMessage
          title={
            e?.response?.data?.message ||
            'Es ist ein unerwarteter Fehler aufgetreten'
          }
        />,
      );
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogForm(null);
    setSelectedOption(null);
  };

  useEffect(() => {
    fetchRegistrationData();
  }, []);

  return (
    <Dialog open={true} onClose={handleCloseDialog}>
      <DialogContent>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography
              component="h1"
              variant="h5"
              sx={{
                my: 3,
                textAlign: 'center',
                fontSize: { xs: '24px', sm: '28px' },
                fontWeight: 500,
                color: 'primary.main',
              }}
            >
              Registriere ein Gerät mittels WebAuthn
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  '& .MuiAlert-message': {
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                  },
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                  Was ist WebAuthn?
                </Typography>
                <Typography paragraph>
                  WebAuthn ermöglicht es Ihnen, Ihr aktuelles Gerät als zweiten
                  Faktor für die Authentifizierung zu verwenden. Dies bietet:
                </Typography>
                <ul style={{ marginTop: 0, paddingLeft: '1.5rem' }}>
                  <li>Erhöhte Sicherheit durch Hardwareschlüssel</li>
                  <li>Einfache und schnelle Anmeldung</li>
                  <li>Keine zusätzliche App erforderlich</li>
                </ul>
                <Typography color="warning.dark" sx={{ mt: 1 }}>
                  Bitte verwenden Sie diese Funktion nicht auf öffentlichen
                  Geräten!
                </Typography>
              </Alert>

              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontSize: '0.9rem',
                  },
                }}
              >
                Um zu überprüfen, ob Ihr lokaler Schlüssel vorhanden ist, können
                Sie unter Windows in den Einstellungen unter „Konten&quot; →
                „Sicherheitsschlüssel&quot; nachsehen, ob ein Schlüssel für
                codemeta.de hinterlegt ist.
              </Alert>
            </Box>

            <Button
              onClick={handleRegister}
              variant="contained"
              size="large"
              startIcon={<LockOpenIcon />}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Jetzt Gerät registrieren
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} color="primary">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
